import { getLanguageNameFromLocale, isGenderedLocale } from 'helpers/locales';
import { useTranslation } from 'react-i18next';
import {
  Button,
  InlineNotification,
  NotificationType,
  SelectionTile,
} from '@spotted-zebra-uk/ui-components';
import Icon from '@/components/atoms/Icon';
import {
  GrammaticalGender,
  SupportedLocale,
} from '../../../generated/graphql';
import { GrammaticalGenderSelection } from './GrammaticalGenderSelection';
import styles from './StepInterviewLanguage.module.scss';

interface StepInterviewLanguageProps {
  selectedLocales: SupportedLocale[];
  selectedLocale?: SupportedLocale;
  onLocaleSelect: (locale: SupportedLocale) => void;
  grammaticalGender: GrammaticalGender;
  onGrammaticalGenderChange: (grammaticalGender: GrammaticalGender) => void;
  onContinue: () => void;
  continueLabel: string;
}

export function StepInterviewLanguage({
  selectedLocales,
  selectedLocale,
  onLocaleSelect,
  grammaticalGender,
  onGrammaticalGenderChange,
  onContinue,
  continueLabel,
}: StepInterviewLanguageProps) {
  const { t } = useTranslation();
  const onlyLocale =
    selectedLocales.length === 1 ? selectedLocales[0] : undefined;
  const resolvedLocale = selectedLocale || onlyLocale;
  const resolvedLocaleName = resolvedLocale
    ? t(`common.languageNames.${resolvedLocale}`)
    : '';

  const grammaticalGenderSelection =
    resolvedLocale && isGenderedLocale(resolvedLocale) ? (
      <GrammaticalGenderSelection
        locale={resolvedLocale}
        value={grammaticalGender}
        onChange={onGrammaticalGenderChange}
      />
    ) : null;

  if (onlyLocale) {
    return (
      <div className={styles.stepLanguage}>
        <div className={styles.content}>
          <h1 className={styles.title}>
            {t('aiInterview.language.confirmTitle')}
          </h1>
          <p className={styles.subtitle}>
            {t('aiInterview.language.confirmSubtitle')}
          </p>
          <InlineNotification
            notificationType={NotificationType.INFORMATION}
            title={t('aiInterview.language.requiredTitle', {
              locale: resolvedLocaleName,
            })}
            className={styles.notification}
            classNameTitle={styles.notificationTitle}
          >
            <p>
              {t('aiInterview.language.requiredBody', {
                locale: resolvedLocaleName,
              })}
            </p>
          </InlineNotification>
          {grammaticalGenderSelection}
        </div>

        <Button
          onClick={onContinue}
          fullWidth
          rightIcon={<Icon icon="right_arrow" />}
        >
          {continueLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.stepLanguage}>
      <div className={styles.content}>
        <h1 className={styles.title}>
          {t('aiInterview.language.chooseTitle')}
        </h1>
        <p className={styles.subtitle}>
          {t('aiInterview.language.chooseSubtitle')}
        </p>
        <div className={styles.options}>
          {selectedLocales.map(locale => {
            const checked = locale === selectedLocale;

            return (
              <SelectionTile
                key={locale}
                id={`ai-interview-language-${locale}`}
                name="ai-interview-language"
                value={locale}
                checked={checked}
                hiddenInputs
                className={styles.optionTile}
                onChange={() => onLocaleSelect(locale)}
              >
                <span className={styles.optionContent}>
                  <span className={styles.optionLabel}>
                    {getLanguageNameFromLocale(locale)}
                  </span>
                  {checked && (
                    <span className={styles.optionCheck} aria-hidden="true" />
                  )}
                </span>
              </SelectionTile>
            );
          })}
        </div>
        {grammaticalGenderSelection}
      </div>

      <p className={styles.helperText}>
        {t('aiInterview.language.cannotChange')}
      </p>
      <Button
        onClick={onContinue}
        disabled={!resolvedLocale}
        fullWidth
        rightIcon={<Icon icon="right_arrow" />}
      >
        {continueLabel}
      </Button>
    </div>
  );
}
